import i18n from "i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import { type DateTime, Settings } from "luxon";
import { initReactI18next } from "react-i18next";
import translationsEN from "./en/en.json";
import translationsES from "./es/es.json";
import translationsHE from "./he/he.json";

const resources = {
	en: { translation: translationsEN },
	es: { translation: translationsES },
	he: { translation: translationsHE },
};

export type SupportedLanguage = keyof typeof resources;

// Object.keys widens to string[]; the keys are exactly the `resources` literal's, so the cast is exact.
export const SUPPORTED_LANGUAGES = Object.keys(resources) as SupportedLanguage[];

export const isSupportedLanguage = (value: string): value is SupportedLanguage => SUPPORTED_LANGUAGES.some((language) => language === value);

i18n
	.use(initReactI18next)
	.use(LanguageDetector)
	.init({
		resources,
		fallbackLng: "en",
		keySeparator: false,
		interpolation: {
			escapeValue: false,
		},
	});

/**
 * Luxon locale handling
 * - Luxon uses Intl under the hood
 * - No updateLocale equivalent; formatting comes from locale data
 */
const localeMap: Record<string, string> = {
	en: "en-US",
	es: "es-US",
	he: "he-US",
};

// Sync Luxon with i18n
i18n.on("languageChanged", (lng) => {
	Settings.defaultLocale = localeMap[lng] ?? "en-US";
});

// Initial locale
Settings.defaultLocale = localeMap[i18n.language] ?? "en-US";

/**
 * Optional helpers if you relied on custom short names in Dayjs
 * (Luxon does not support mutating locale data)
 */
export const formatWeekdayShort = (dt: DateTime) => dt.setLocale(Settings.defaultLocale).toFormat("ccc");

export const formatMonthShort = (dt: DateTime) => dt.setLocale(Settings.defaultLocale).toFormat("LLL");

export default i18n;
